<script setup lang="ts">
import { $getSignTypes } from '@/api/signs'
import { uniqBy } from 'lodash'

interface IProps {
  modelValue?: any,
  label?: string,
  selectOptions?: any[] | null,
  /**
   * Optional array of signType IDs that should be disabled for selection.
   * If null or undefined, no event groups will be disabled.
   */
  disabledSignTypes?: number[] | null,
}

const props = withDefaults(defineProps<IProps>(), {
  modelValue: null,
  label: 'Sign Type',
  selectOptions: null,
  disabledSignTypes: null,
})

interface CustomEvents {
  'update:modelValue': (newValue: any) => void;
}

const emit = defineEmits<CustomEvents>()

interface SignType {
  id: number
  name: string
}

const signTypes = ref<SignType[]>([])
const signTypesCurrentPage = ref<number>(1)
const signTypesPerPage: number = 10
const signTypesSearchStr = ref<string>('')
const isNextSignTypesPageExist = ref<boolean>(false)
const signTypesLoading = ref<boolean>(false)

const signTypeOptions = computed(() => {
  if (props.selectOptions) {
    return props.selectOptions
  } else {
    return signTypes.value
  }
})

const getSignTypes = async () => {
  signTypesLoading.value = true
  try {
    const res = await $getSignTypes({
      search: signTypesSearchStr.value,
      per_page: signTypesPerPage,
      page: signTypesCurrentPage.value,
    })

    // Use Lodash's uniqBy to merge arrays while ensuring uniqueness based on 'id'
    signTypes.value = uniqBy([...signTypes.value, ...res.data.results], 'id')

    isNextSignTypesPageExist.value = !!res.data.next
  } catch (e) {
    console.log(e)
  } finally {
    signTypesLoading.value = false
  }
}

const onFocusSignTypeSelect = async () => {
  if (!signTypes.value.length && !props.selectOptions) {
    signTypes.value = []
    signTypesCurrentPage.value = 1
    await getSignTypes()
  }
}

const onLoadMoreSignTypes = async () => {
  signTypesCurrentPage.value++ // Increment the current page to load the next page of signTypes
  await getSignTypes()
}

const clearAndGetSignTypes = (searchStr: string) => {
  signTypes.value = []
  signTypesCurrentPage.value = 1 // Set current page => 1 to load initial first page of signTypes
  signTypesSearchStr.value = searchStr
  getSignTypes()
}

const handleSignTypeSearch = useDebounceFn((searchStr: string) => {
  if (!props.selectOptions) {
    clearAndGetSignTypes(searchStr)
  }
}, 500)

defineExpose({ clearAndGetSignTypes })
</script>

<template>
  <VAutocomplete
    :model-value="modelValue"
    :items="[...signTypeOptions]"
    return-object
    item-title="name"
    item-value="id"
    :label="label"
    density="compact"
    :no-filter="!selectOptions"
    multiple
    clearable
    @update:modelValue="newValue => $emit('update:modelValue', newValue)"
    @update:focused="onFocusSignTypeSelect"
    @update:search="handleSignTypeSearch"
  >
    <template
      v-if="disabledSignTypes"
      #item="{ item, index, props }"
    >
      <VListItem
        v-bind="props"
        :disabled="!!disabledSignTypes ? disabledSignTypes.includes(item?.value) : false"
      />
    </template>
    <template #selection="{ item, index }">
      <div class="d-flex align-center">
        <v-chip v-if="index < 1">
          <span>{{ item.title }}</span>
        </v-chip>
        <span
          v-if="index === 1"
          class="text-grey text-caption align-self-center"
        >
          (+{{ modelValue?.length - 1 }} others)
        </span>
      </div>
    </template>
    <template #no-data>
      <div
        v-if="!!signTypesLoading"
        class="d-flex justify-center align-center"
      >
        Loading...
        <VProgressCircular
          class="margin-left-5"
          size="22"
          indeterminate
          color="white"
        />
      </div>
      <div
        v-else
        class="d-flex justify-center align-center"
      >
        Sign Types not found
      </div>
    </template>
    <template
      v-if="isNextSignTypesPageExist"
      #append-item
    >
      <VBtn
        color="default"
        block
        :disabled="!!signTypesLoading"
        @click="onLoadMoreSignTypes"
      >
        Load more...
        <VProgressCircular
          v-if="!!signTypesLoading"
          class="margin-left-5"
          size="22"
          indeterminate
          color="white"
        />
      </VBtn>
    </template>
  </VAutocomplete>
</template>

<style lang="scss">

</style>
